import { type DependencyList, useMemo, useRef } from 'react';

import type { EqualFunction } from '#misc/compare/types/equal-function.type';

/**
 * A custom hook that memoizes the dependencies using a custom equality function.
 * It returns the same reference if the dependencies are equal, otherwise it updates the reference.
 *
 * @param dependencies - The dependencies to compare.
 * @param equals - The custom equality function to compare the dependencies.
 * @returns The memoized dependencies.
 */
export const useCompareMemoize = (dependencies: DependencyList, equals: EqualFunction<DependencyList>) => {
  const ref = useRef(dependencies);
  const signalRef = useRef(0);

  if (!equals(dependencies, ref.current)) {
    ref.current = dependencies;
    signalRef.current += 1;
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: the signalRef is used to trigger re-computation
  return useMemo(() => {
    return ref.current;
  }, [signalRef.current]);
};
